import { CollisionDetection, DndContext, DragEndEvent, DragOverEvent, MeasuringStrategy, MouseSensor, TouchSensor, UniqueIdentifier, closestCenter, closestCorners, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import debounce from 'lodash.debounce';
import { changeLecturePosition } from 'modules/courses/courses/curriculum/api/lecture-api';
import { changeModulePosition } from 'modules/courses/courses/curriculum/api/module-api';
import CreateLectureModal from 'modules/courses/courses/curriculum/components/create-lecture-modal';
import { ModuleLecture } from 'modules/courses/courses/curriculum/components/module-lecture';
import { CourseCurriculumCommonInterface, CourseCurriculumInterface } from 'modules/courses/courses/curriculum/types/curriculum-interface';
import { ModuleInterface } from 'modules/courses/courses/curriculum/types/module-interface';
import { getSelectedСoursesCurriculumFromStorage, setSelectedСoursesCurriculumToStorage } from 'modules/courses/courses/curriculum/utils/storage-utils';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { KeyedMutator } from 'swr';
import { LightCourseLectureActions } from '../../light-course/components/light-course-lecture-actions';
import { LightCourseModuleActions } from '../../light-course/components/light-course-module-actions';
import { LightCourseCurriculumInterface } from '../../light-course/light-course-interface';
import { CourseId } from '../../types/course-interface';
import { LectureId } from '../types/lecture-interface';
import { nonDraggableProps } from '../utils/non-draggable-props';
import { AddLectureButton, AddLectureButtonProps } from './add-lecture-button';
import { CourseModule } from './course-module';
import LectureActions from './lecture-actions';
import ModuleActions from './module-actions';
type LightCourseProps = {
  isLightCourse: true;
  modules: LightCourseCurriculumInterface['modules'];
  mutate: KeyedMutator<LightCourseCurriculumInterface>;
  onAddLectureClick: (moduleId: number, setFetching: Parameters<AddLectureButtonProps['onClick']>[0]) => void;
  onEditLecture: (lectureId: LectureId) => void;
  onLectureNameClick: (lectureId: LectureId) => void;
};
type CourseProps = {
  isLightCourse?: false;
  modules: ModuleInterface[];
  mutate: KeyedMutator<CourseCurriculumInterface>;
  onAddLectureClick?: never;
  onEditLecture?: never;
  onLectureNameClick?: never;
};
export type CourseCurriculumTableProps = {
  courseId: CourseId;
} & (LightCourseProps | CourseProps);
function CourseCurriculumTable({
  courseId,
  isLightCourse,
  modules,
  mutate,
  onAddLectureClick,
  onEditLecture,
  onLectureNameClick
}: CourseCurriculumTableProps) {
  const activeIdRef = useRef<UniqueIdentifier | null>(null);
  const clonedItemsRef = useRef<typeof modules | null>(null);
  const movingToNewModuleRef = useRef(false);
  const [lecturesChangingPosition, setLecturesChangingPosition] = useState<number[]>([]);
  const containers = modules.map(module => module.id) as UniqueIdentifier[];
  const checkIfContainer = (element: {
    id: UniqueIdentifier;
  }) => containers.includes(element.id);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const selectedСoursesCurriculumCookie = useMemo(() => getSelectedСoursesCurriculumFromStorage(courseId), [courseId]);

  /**
   * @description spread inner content as its type contains only intersection of types
   */
  const sortingMutator = mutate as unknown as KeyedMutator<CourseCurriculumCommonInterface>;
  const collisionDetectionStrategy = useCallback(debounce<CollisionDetection>(args => {
    if (activeIdRef.current && checkIfContainer({
      id: activeIdRef.current
    })) {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(checkIfContainer)
      });
    }
    return closestCorners(args);
  }, 50, {
    leading: true,
    trailing: false,
    maxWait: 50
  }), []);
  const findContainer = (id: UniqueIdentifier) => {
    if (containers.includes(id)) {
      return id;
    }
    return modules.find(module_0 => {
      const searchLecture = module_0.lectures.find(lecture => lecture.id === id);
      if (searchLecture) {
        return module_0;
      }
    })?.id;
  };
  const onDragCancel = async () => {
    const clonedItems = clonedItemsRef.current;
    if (clonedItems) {
      await sortingMutator(data => {
        if (data) {
          return {
            ...data,
            modules: clonedItems
          };
        }
      });
    }
    activeIdRef.current = null;
    clonedItemsRef.current = null;
    movingToNewModuleRef.current = false;
  };
  const onDragEnd = async ({
    active,
    over
  }: DragEndEvent) => {
    const overId = over?.id;
    if (containers.includes(active.id) && overId) {
      const activeIndex = modules.findIndex(module_1 => module_1.id === active.id);
      const overIndex = modules.findIndex(module_2 => module_2.id === over.id);
      if (activeIndex === overIndex) {
        activeIdRef.current = null;
        clonedItemsRef.current = null;
        movingToNewModuleRef.current = false;
        return;
      }
      await sortingMutator(data_0 => {
        if (data_0) {
          return {
            ...data_0,
            modules: arrayMove(data_0.modules, activeIndex, overIndex)
          };
        }
      }, false);
      await changeModulePosition(Number(active.id), {
        new_position: overIndex + 1
      });
      activeIdRef.current = null;
      clonedItemsRef.current = null;
      movingToNewModuleRef.current = false;
      return;
    }
    const activeContainer = findContainer(active.id);
    if (!activeContainer || overId == null) {
      activeIdRef.current = null;
      clonedItemsRef.current = null;
      movingToNewModuleRef.current = false;
      return;
    }
    const overContainer = findContainer(overId);
    if (!overContainer) {
      activeIdRef.current = null;
      clonedItemsRef.current = null;
      movingToNewModuleRef.current = false;
      return;
    }
    const moduleOver = modules.find(module_3 => module_3.id === overContainer);
    const moduleActive = modules.find(module_4 => module_4.id === activeContainer);
    if (!moduleOver || !moduleActive) {
      activeIdRef.current = null;
      clonedItemsRef.current = null;
      movingToNewModuleRef.current = false;
      return;
    }
    const activeIndex_0 = moduleActive.lectures.findIndex(lecture_0 => lecture_0.id === active.id);
    const overIndex_0 = moduleOver.lectures.findIndex(lecture_1 => lecture_1.id === overId);
    if (activeIndex_0 === overIndex_0 && activeContainer === overContainer && !movingToNewModuleRef.current) {
      activeIdRef.current = null;
      clonedItemsRef.current = null;
      movingToNewModuleRef.current = false;
      return;
    }
    if (activeIndex_0 === -1 || overIndex_0 === -1) {
      activeIdRef.current = null;
      clonedItemsRef.current = null;
      movingToNewModuleRef.current = false;
      return;
    }
    await sortingMutator(data_1 => {
      if (data_1) {
        if (moduleOver.id === moduleActive.id) {
          return {
            ...data_1,
            modules: data_1.modules.map(module_5 => {
              if (activeIndex_0 > module_5.lectures.length - 1 || overIndex_0 > module_5.lectures.length - 1) {
                return module_5;
              }
              if (module_5.id === overContainer) {
                return {
                  ...module_5,
                  lectures: arrayMove(module_5.lectures, activeIndex_0, overIndex_0)
                };
              }
              return module_5;
            })
          };
        } else {
          return data_1;
        }
      }
      return data_1;
    }, false);
    try {
      setLecturesChangingPosition(prev => [...prev, Number(active.id)]);
      if (movingToNewModuleRef.current) {
        await changeLecturePosition(Number(active.id), {
          new_position: overIndex_0 + 1,
          module: moduleOver.id
        });
      } else {
        await changeLecturePosition(Number(active.id), {
          new_position: overIndex_0 + 1
        });
      }
    } catch (error) {
      if (clonedItemsRef.current) {
        await sortingMutator(data_2 => {
          if (data_2) {
            const originalActiveModule = clonedItemsRef.current?.find(m => m.id === activeContainer);
            const originalOverModule = clonedItemsRef.current?.find(m_0 => m_0.id === overContainer);
            if (originalActiveModule && originalOverModule) {
              return {
                ...data_2,
                modules: data_2.modules.map(module_6 => {
                  if (module_6.id === activeContainer) {
                    return {
                      ...module_6,
                      lectures: originalActiveModule.lectures
                    };
                  }
                  if (module_6.id === overContainer && activeContainer !== overContainer) {
                    return {
                      ...module_6,
                      lectures: originalOverModule.lectures
                    };
                  }
                  return module_6;
                })
              };
            }
          }
          return data_2;
        }, false);
      }
    } finally {
      setLecturesChangingPosition(prev => prev.filter(el => el !== Number(active.id)));
    }
    activeIdRef.current = null;
    clonedItemsRef.current = null;
    movingToNewModuleRef.current = false;
  };
  const onDragOver = async ({
    active: active_0,
    over: over_0
  }: DragOverEvent) => {
    const overId_0 = over_0?.id;
    if (overId_0 === undefined || overId_0 === null || containers.includes(active_0.id)) {
      return;
    }
    const overContainerId = findContainer(overId_0);
    const activeContainerId = findContainer(active_0.id);
    if (!overContainerId || !activeContainerId) {
      return;
    }
    if (activeContainerId !== overContainerId) {
      movingToNewModuleRef.current = true;
      const activeModule = modules.find(module_7 => module_7.id === activeContainerId);
      const overModule = modules.find(module_8 => module_8.id === overContainerId);
      const activeItems = activeModule?.lectures;
      const overItems = overModule?.lectures;
      const overIndex_1 = overItems?.findIndex(el => el.id === overId_0);
      const activeIndex_1 = activeItems?.findIndex(el_0 => el_0.id === active_0.id);
      let newIndex: number | undefined = undefined;
      if (containers.includes(overId_0) && overItems) {
        newIndex = overItems.length;
      } else {
        const isBelowOverItem = over_0 && active_0.rect.current.translated && active_0.rect.current.translated.top > over_0.rect.top + over_0.rect.height;
        const modifier = isBelowOverItem ? 1 : 0;
        if (overItems) {
          newIndex = overIndex_1 && overIndex_1 >= 0 ? overIndex_1 + modifier : overItems.length;
        }
      }
      await sortingMutator(data_3 => {
        if (data_3 && typeof newIndex === 'number' && activeModule && typeof activeIndex_1 === 'number') {
          const activeLecture = activeModule.lectures[activeIndex_1];
          if (!activeLecture) return data_3;
          return {
            ...data_3,
            modules: data_3.modules.map(module_9 => {
              if (module_9.id === activeContainerId) {
                return {
                  ...module_9,
                  lectures: module_9.lectures.filter(item => item.id !== active_0.id)
                };
              }
              if (module_9.id === overContainerId) {
                return {
                  ...module_9,
                  lectures: [...module_9.lectures.slice(0, newIndex), activeLecture, ...module_9.lectures.slice(newIndex)]
                };
              }
              return module_9;
            })
          };
        }
        return data_3;
      }, false);
    }
  };
  function onSelectCoursesCurriculum(CurriculumId: number) {
    setSelectedСoursesCurriculumToStorage(courseId, CurriculumId);
  }
  return <DndContext sensors={sensors} collisionDetection={collisionDetectionStrategy} measuring={{
    droppable: {
      strategy: MeasuringStrategy.Always
    }
  }} onDragStart={({
    active: active_1
  }) => {
    activeIdRef.current = active_1.id;
    clonedItemsRef.current = JSON.parse(JSON.stringify(modules));
    movingToNewModuleRef.current = false;
  }} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
      <SortableContext items={modules} strategy={verticalListSortingStrategy}>
        <div className="mt-5 flex flex-col gap-5">
          {isLightCourse ? modules.map(module_10 => <CourseModule key={module_10.id} module={module_10} childrenCount={module_10.lectures.length} openDefault={module_10.id === Number(selectedСoursesCurriculumCookie)} moduleActions={<LightCourseModuleActions module={module_10} mutate={mutate} />} onSelect={onSelectCoursesCurriculum}>
                  <SortableContext items={module_10.lectures} strategy={verticalListSortingStrategy}>
                    {module_10.lectures.map((lecture_2, index) => {
              return <ModuleLecture key={lecture_2.id} disabled={lecturesChangingPosition.includes(lecture_2.id)} isFetching={lecturesChangingPosition.includes(lecture_2.id)} lecture={lecture_2} isLastChild={index === module_10.lectures.length - 1} data-test={`module-${module_10.id}-lecture-${lecture_2.id}`} onNameClick={e => {
                e.preventDefault();
                onLectureNameClick(lecture_2.id);
              }} lectureActions={<LightCourseLectureActions onEdit={() => onEditLecture(lecture_2.id)} mutate={mutate} lecture={lecture_2} moduleId={module_10.id} />} />;
            })}
                    <div className="align-center flex justify-center py-5">
                      <div {...nonDraggableProps}>
                        <AddLectureButton onClick={setFetching => onAddLectureClick(module_10.id, setFetching)} data-test={'module-create-lecture'} />
                      </div>
                    </div>
                  </SortableContext>
                </CourseModule>) : modules.map(module_11 => <CourseModule key={module_11.id} module={module_11} childrenCount={module_11.lectures.length} openDefault={module_11.id === Number(selectedСoursesCurriculumCookie)} moduleActions={<ModuleActions module={module_11} mutate={mutate} />} onSelect={onSelectCoursesCurriculum}>
                  <SortableContext items={module_11.lectures} strategy={verticalListSortingStrategy}>
                    {module_11.lectures.map((lecture_3, index_0) => {
              return <ModuleLecture key={lecture_3.id} disabled={lecturesChangingPosition.includes(lecture_3.id)} isFetching={lecturesChangingPosition.includes(lecture_3.id)} lecture={lecture_3} isLastChild={index_0 === module_11.lectures.length - 1} data-test={`module-${module_11.id}-lecture-${lecture_3.id}`} lectureActions={<LectureActions moduleId={module_11.id} lecture={lecture_3} mutate={mutate} />} />;
            })}
                    <div className="align-center flex justify-center py-5">
                      <div {...nonDraggableProps}>
                        <CreateLectureModal moduleId={module_11.id} mutate={mutate} />
                      </div>
                    </div>
                  </SortableContext>
                </CourseModule>)}
        </div>
      </SortableContext>
    </DndContext>;
}
export default CourseCurriculumTable;